import { HOC_MDXWrapper } from 'nextra/setup-page'
import { pageMap } from '/ui/docs-site/.next/static/chunks/nextra-page-map-.mjs'
/*@jsxRuntime automatic*/
/*@jsxImportSource react*/
/*-- dri: Garrett Guillotte --*/
import {useMDXComponents as _provideComponents} from "nextra/mdx";
const title = "Prometheus API overview";
const frontMatter = {
  "title": "Prometheus API overview",
  "description": "How to access and use Prometheus API endpoints for programmatic access to your observability data."
};
import {Alert} from '@/components';
import ApiSupportedEndpoints, {useTOC as useTOC0} from '@/_partials/api-supported-endpoints.mdx';
export function useTOC(props) {
  const _components = {
    code: "code",
    ..._provideComponents()
  };
  return [...useTOC0(), {
    value: "Authentication",
    id: "authentication",
    depth: 2
  }, {
    value: "Creating an API token",
    id: "creating-an-api-token",
    depth: 3
  }, {
    value: "Using an API token",
    id: "using-an-api-token",
    depth: 3
  }, {
    value: "Prometheus HTTP API",
    id: "prometheus-http-api",
    depth: 2
  }, {
    value: "Supported headers for Prometheus HTTP API extensions",
    id: "supported-headers-for-prometheus-http-api-extensions",
    depth: 3
  }, {
    value: <><_components.code>{"M3-Restrict-By-Tags-JSON"}</_components.code></>,
    id: "m3-restrict-by-tags-json",
    depth: 4
  }, {
    value: "Prometheus remote read API",
    id: "prometheus-remote-read-api",
    depth: 2
  }, {
    value: "Viewing raw values as JSON",
    id: "viewing-raw-values-as-json",
    depth: 3
  }, {
    value: "Using Chronosphere as a data source for your own Grafana deployment",
    id: "using-chronosphere-as-a-data-source-for-your-own-grafana-deployment",
    depth: 2
  }];
}
function MDXLayout(props) {
  const {toc = useTOC(props)} = props;
  const _components = {
    a: "a",
    code: "code",
    h1: "h1",
    h2: "h2",
    h3: "h3",
    h4: "h4",
    li: "li",
    p: "p",
    pre: "pre",
    span: "span",
    strong: "strong",
    ul: "ul",
    ..._provideComponents(),
    ...props.components
  };
  return <>{}{"\n"}<_components.h1>{"Prometheus API overview"}</_components.h1>{"\n"}<_components.p>{"Chronosphere exposes Prometheus API endpoints to authorized users."}</_components.p>{"\n"}<_components.p>{"For information about the Chronosphere API, see\n"}<_components.a href="/tooling/api-info">{"Get started with the Chronosphere API"}</_components.a>{"."}</_components.p>{"\n"}<ApiSupportedEndpoints />{"\n"}<_components.h2 id={toc[0].id}>{toc[0].value}</_components.h2>{"\n"}<_components.p>{"Use of the Prometheus API requires you to authenticate with an API token."}</_components.p>{"\n"}<_components.h3 id={toc[1].id}>{toc[1].value}</_components.h3>{"\n"}<_components.p>{"A service can access the Prometheus API by authenticating with its API token. For\ndetails, see "}<_components.a href="/administer/accounts-teams/service-accounts">{"Service Accounts"}</_components.a>{"."}</_components.p>{"\n"}<_components.p>{"A user can access the Prometheus API by creating a temporary\n"}<_components.a href="/administer/accounts-teams/personal-access-tokens">{"personal access token"}</_components.a>{"."}</_components.p>{"\n"}<_components.h3 id={toc[2].id}>{toc[2].value}</_components.h3>{"\n"}<_components.p>{"You can use an API token to make HTTP API calls."}</_components.p>{"\n"}<_components.p>{"For HTTP calls, use the "}<_components.code>{"Authorization"}</_components.code>{" header with the "}<_components.code>{"Bearer"}</_components.code>{" token authentication\nscheme."}</_components.p>{"\n"}<_components.p>{"Example:"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="shell" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#6F42C1",
    "--shiki-light-font-style": "inherit"
  }}>{"curl"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" -H"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"Authorization: Bearer "}</_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#22863A"
  }}>{"${CHRONOSPHERE_API_TOKEN}"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"https://"}</_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#22863A"
  }}>{"${CHRONOSPHERE_DOMAIN}"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{".chronosphere.io/data/metrics/api/v1/query?query=up\""}</_components.span></_components.span></_components.code></_components.pre>{"\n"}<_components.h2 id={toc[3].id}>{toc[3].value}</_components.h2>{"\n"}<_components.p>{"For the API documentation for the routes, see "}<_components.a href="https://prometheus.io/docs/prometheus/latest/querying/api">{"the Prometheus website"}</_components.a>{"."}</_components.p>{"\n"}<Alert severity="info"><_components.p>{"Chronosphere supports only the instant query, range query, series metadata and labels\nendpoints."}</_components.p></Alert>{"\n"}<_components.p>{"When accessing the Prometheus HTTP API, the URL begins with\n"}<_components.code>{"https://${CHRONOSPHERE_DOMAIN}.chronosphere.io/data/metrics/_"}</_components.code>{"."}</_components.p>{"\n"}<_components.p>{"Here’s an example of a request to the instant query endpoint:"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="shell" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#6F42C1",
    "--shiki-light-font-style": "inherit"
  }}>{"curl"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" -H"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"Authorization: Bearer "}</_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#22863A"
  }}>{"${CHRONOSPHERE_API_TOKEN}"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#24292EFF"
  }}>{" \\"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"  \"https://"}</_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#22863A"
  }}>{"${CHRONOSPHERE_DOMAIN}"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{".chronosphere.io/data/metrics/api/v1/query?query=up\""}</_components.span></_components.span></_components.code></_components.pre>{"\n"}<_components.h3 id={toc[4].id}>{toc[4].value}</_components.h3>{"\n"}<_components.p>{"There are a set of supported headers for extensions to the Prometheus HTTP API."}</_components.p>{"\n"}<_components.h4 id={toc[5].id}>{toc[5].value}</_components.h4>{"\n"}<_components.p>{"This header restricts metrics fetched and returned for queries specified by the\nrequest."}</_components.p>{"\n"}<_components.p>{"Each key as part of the options JSON is optional. Here’s a complete JSON example:"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="json" data-word-wrap=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"  \""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"match"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{" ["}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"    {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"      \""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"name"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"label-name\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"      \""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"type"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"EQUAL\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"      \""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"value"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"label-value\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"    }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"  ]"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"  \""}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"strip"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#D32F2F"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{" ["}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"label-name\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"]"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"}"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}<_components.ul>{"\n"}<_components.li>{"\n"}<_components.p><_components.code>{"match"}</_components.code>{": A set of match restrictions to apply to every fetch that’s a part of a\nquery, ensuring every fetched series satisfies these restrictions."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p><_components.code>{"match[].name"}</_components.code>{": The label name to restrict metric fetches by."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p><_components.code>{"match[].value"}</_components.code>{": The label value to restrict metric fetches by."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p><_components.code>{"match[].type"}</_components.code>{": The type to restrict metric fetches by. Must be one of these\nvalues:"}</_components.p>{"\n"}<_components.ul>{"\n"}<_components.li><_components.code>{"EQUAL"}</_components.code></_components.li>{"\n"}<_components.li><_components.code>{"NOTEQUAL"}</_components.code></_components.li>{"\n"}<_components.li><_components.code>{"REGEXP"}</_components.code></_components.li>{"\n"}<_components.li><_components.code>{"NOTREGEXP"}</_components.code></_components.li>{"\n"}<_components.li><_components.code>{"EXISTS"}</_components.code></_components.li>{"\n"}<_components.li><_components.code>{"NOTEXISTS"}</_components.code></_components.li>{"\n"}</_components.ul>{"\n"}<_components.p>{"The types "}<_components.code>{"EXISTS"}</_components.code>{" and "}<_components.code>{"NOTEXISTS"}</_components.code>{" apply only to the label name. All other types\nrequire you set both the label name and a value."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p><_components.code>{"strip[]"}</_components.code>{": A set of label names to remove from the query response. By default, no\nlabels are stripped from queries. If "}<_components.code>{"match[]"}</_components.code>{" is set, this defaults to\n"}<_components.code>{"match[].name"}</_components.code>{" (the unique set of label names from the provided matchers). If you\ndon’t want to strip the labels, set "}<_components.code>{"strip"}</_components.code>{" to an empty list (such as "}<_components.code>{"\"strip\": []"}</_components.code>{").\nAs such, "}<_components.code>{"strip"}</_components.code>{" behaves in the following ways:"}</_components.p>{"\n"}<_components.ul>{"\n"}<_components.li>{"With no set restrict by tags header, no labels are stripped from the response\nresults."}</_components.li>{"\n"}<_components.li>{"When a restrict by tags header set with "}<_components.code>{"match"}</_components.code>{" is specified and "}<_components.code>{"strip"}</_components.code>{" isn’t\nspecified, it strips any labels requiring a match as defined by "}<_components.code>{"match[].name"}</_components.code>{"\nvalues. For example, "}<_components.code>{"{\"match\":[{\"name\":\"item\",\"type\":\"EQUAL\",\"value\":\"bar\"}]}"}</_components.code>{"\nstrips the "}<_components.code>{"item"}</_components.code>{" label from all time series the metadata returns."}</_components.li>{"\n"}<_components.li>{"When a restrict by tags header is set with "}<_components.code>{"strip"}</_components.code>{" specified, it strips exactly\nonly those labels regardless of other parameters. For example,\n"}<_components.code>{"{\"match\":[{\"name\":\"item\",\"type\":\"EQUAL\",\"value\":\"bar\"}],\"strip\":[]}"}</_components.code>{" doesn’t\nstrip any labels from the response results because "}<_components.code>{"[]"}</_components.code>{" is specified."}</_components.li>{"\n"}</_components.ul>{"\n"}</_components.li>{"\n"}</_components.ul>{"\n"}<_components.p><_components.strong>{"Example: Restrict by "}<_components.code>{"org"}</_components.code>{" label"}</_components.strong></_components.p>{"\n"}<_components.p>{"This adds a restriction to all queries that they must include "}<_components.code>{"org=\"company\""}</_components.code>{" as part of\nthe underlying response. All "}<_components.code>{"org"}</_components.code>{" labels on resulting metrics are also stripped from\nthe response."}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="shell" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"RESTRICT"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{"="}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"$("}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#6F42C1",
    "--shiki-light-font-style": "inherit"
  }}>{"cat"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{" <<"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#24292EFF"
  }}>{"'EOF'"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"{"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"  \"match\": ["}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"    {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"      \"name\": \"org\","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"      \"value\": \"company\","}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"      \"type\": \"EQUAL\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"    }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"  ]"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"}"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#24292EFF"
  }}>{"EOF"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{")"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#6F42C1",
    "--shiki-light-font-style": "inherit"
  }}>{"curl"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#24292EFF"
  }}>{" \\"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{"  -H"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"Authorization: Bearer "}</_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#22863A"
  }}>{"${CHRONOSPHERE_API_TOKEN}"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#24292EFF"
  }}>{" \\"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{"  -H"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"M3-Restrict-By-Tags-JSON: "}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#22863A"
  }}>{"$("}</_components.span><_components.span style={{
    "--shiki-dark": "#ED8796",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#6F42C1",
    "--shiki-light-font-style": "inherit"
  }}>{"echo"}</_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#22863A"
  }}>{" $RESTRICT"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{" |"}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#6F42C1",
    "--shiki-light-font-style": "inherit"
  }}>{" tr"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" -d"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" ' '"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#22863A"
  }}>{")"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#24292EFF"
  }}>{" \\"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"  \"https://"}</_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#22863A"
  }}>{"${CHRONOSPHERE_DOMAIN}"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{".chronosphere.io/data/metrics/api/v1/query?query=up\""}</_components.span></_components.span></_components.code></_components.pre>{"\n"}<_components.h2 id={toc[6].id}>{toc[6].value}</_components.h2>{"\n"}<_components.p>{"The Prometheus remote read API implements the Snappy compressed binary Protobuf\n"}<_components.a href="https://prometheus.io/docs/prometheus/latest/configuration/configuration/#remote_read">{"Remote Read"}</_components.a>{"\nendpoint, which provides debug capabilities that make raw vector selector data\navailable from a PromQL query in a JSON format."}</_components.p>{"\n"}<_components.p>{"When accessing the Prometheus Remote Read API, the URL is\n"}<_components.code>{"https://${CHRONOSPHERE_DOMAIN}.chronosphere.io/data/metrics/api/v1/prom/remote/read"}</_components.code>{"."}</_components.p>{"\n"}<_components.h3 id={toc[7].id}>{toc[7].value}</_components.h3>{"\n"}<_components.p>{"To read raw values from storage and avoid any PromQL evaluation you can set the\n"}<_components.code>{"start"}</_components.code>{" (Unix timestamp seconds), "}<_components.code>{"end"}</_components.code>{" (Unix timestamp seconds), "}<_components.code>{"format"}</_components.code>{" ("}<_components.code>{"json"}</_components.code>{")\nand "}<_components.code>{"query"}</_components.code>{" (any valid PromQL query) URL query parameters and call the Remote Read\nAPI endpoint."}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="shell" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"END"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{"="}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"$("}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#6F42C1",
    "--shiki-light-font-style": "inherit"
  }}>{"date"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" +%s"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{")"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"START"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{"="}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"$("}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#6F42C1",
    "--shiki-light-font-style": "inherit"
  }}>{"expr"}</_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{" $END "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{"-"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{" 600"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{")"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#6F42C1",
    "--shiki-light-font-style": "inherit"
  }}>{"curl"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" -H"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"Authorization: Bearer "}</_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#22863A"
  }}>{"${CHRONOSPHERE_API_TOKEN}"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\""}</_components.span><_components.span style={{
    "--shiki-dark": "#F5BDE6",
    "--shiki-light": "#24292EFF"
  }}>{" \\"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"  \"https://"}</_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#22863A"
  }}>{"${CHRONOSPHERE_DOMAIN}"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{".chronosphere.io/data/metrics/api/v1/prom/remote/read?start="}</_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#22863A"
  }}>{"${START}"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"&end="}</_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#22863A"
  }}>{"${END}"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"&format=json&query=up\""}</_components.span></_components.span></_components.code></_components.pre>{"\n"}<_components.h2 id={toc[8].id}>{toc[8].value}</_components.h2>{"\n"}<_components.p>{"Some times you might want to maintain an internal deployment of Grafana and point it\nto the Chronosphere backend. To do this, set up a "}<_components.code>{"Prometheus"}</_components.code>{" data source with the\nfollowing configurations:"}</_components.p>{"\n"}<_components.ul>{"\n"}<_components.li>{"\n"}<_components.p>{"Name: "}<_components.code>{"Chronosphere"}</_components.code></_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"URL: "}<_components.code>{"https://${CHRONOSPHERE_DOMAIN}.chronosphere.io/data/metrics"}</_components.code></_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Access: "}<_components.code>{"Server (Default)"}</_components.code></_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Auth: "}<_components.code>{"Basic Auth"}</_components.code></_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Basic Auth Details:"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="text" data-word-wrap=""><_components.code><_components.span><_components.span>{"User: chronosphere"}</_components.span></_components.span>{"\n"}<_components.span><_components.span>{"Password: $CHRONOSPHERE_API_TOKEN"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}</_components.li>{"\n"}</_components.ul></>;
}


export default HOC_MDXWrapper(
  MDXLayout,
  '/tooling/prometheus-api',
  {"filePath":"pages/tooling/prometheus-api.mdx",pageMap,frontMatter,title},
  typeof RemoteContent === 'undefined' ? useTOC : RemoteContent.useTOC
)